import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import {
  useCallback
} from 'react';

import type {
  GetBranchesByCountryCodeResponse,
  GetFilterableBranchesResponse,
  ImportBranchAssignmentsBody,
  ProblemDetails
} from '../../models';

import { useCustomWelcomeClient } from '../../custom-client.tsx';
import type { ErrorType } from '../../custom-client.tsx';

type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;




const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Returns a list of branches assigned to requests of the current user<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding-backoffice
 */
export const useGetFilterableBranchesHook = () => {
        const getFilterableBranches = useCustomWelcomeClient<GetFilterableBranchesResponse>();

        return useCallback((

 signal?: AbortSignal
) => {


        return getFilterableBranches(
          {url: `/branches`, method: 'GET', signal
    },
          );
        }, [getFilterableBranches])
      }




export const getGetFilterableBranchesQueryKey = () => {
    return [
    `/branches`
    ] as const;
    }


export const useGetFilterableBranchesQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>, TError = ErrorType<ProblemDetails>>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetFilterableBranchesQueryKey();

  const getFilterableBranches =  useGetFilterableBranchesHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>> = ({ signal }) => getFilterableBranches(signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetFilterableBranchesQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>>
export type GetFilterableBranchesQueryError = ErrorType<ProblemDetails>


export function useGetFilterableBranches<TData = Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>, TError = ErrorType<ProblemDetails>>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetFilterableBranches<TData = Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>, TError = ErrorType<ProblemDetails>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetFilterableBranches<TData = Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>, TError = ErrorType<ProblemDetails>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetFilterableBranches<TData = Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>, TError = ErrorType<ProblemDetails>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetFilterableBranchesHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetFilterableBranchesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Returns branches by country code<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useGetBranchesByCountryCodeHook = () => {
        const getBranchesByCountryCode = useCustomWelcomeClient<GetBranchesByCountryCodeResponse>();

        return useCallback((
    countryCode: string,
 signal?: AbortSignal
) => {


        return getBranchesByCountryCode(
          {url: `/branches/country/${countryCode}`, method: 'GET', signal
    },
          );
        }, [getBranchesByCountryCode])
      }




export const getGetBranchesByCountryCodeQueryKey = (countryCode: string,) => {
    return [
    `/branches/country/${countryCode}`
    ] as const;
    }


export const useGetBranchesByCountryCodeQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>, TError = ErrorType<ProblemDetails>>(countryCode: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetBranchesByCountryCodeQueryKey(countryCode);

  const getBranchesByCountryCode =  useGetBranchesByCountryCodeHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>> = ({ signal }) => getBranchesByCountryCode(countryCode, signal);





   return  { queryKey, queryFn, enabled: countryCode !== null && countryCode !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetBranchesByCountryCodeQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>>
export type GetBranchesByCountryCodeQueryError = ErrorType<ProblemDetails>


export function useGetBranchesByCountryCode<TData = Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>, TError = ErrorType<ProblemDetails>>(
 countryCode: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetBranchesByCountryCode<TData = Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>, TError = ErrorType<ProblemDetails>>(
 countryCode: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetBranchesByCountryCode<TData = Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>, TError = ErrorType<ProblemDetails>>(
 countryCode: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetBranchesByCountryCode<TData = Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>, TError = ErrorType<ProblemDetails>>(
 countryCode: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetBranchesByCountryCodeHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetBranchesByCountryCodeQueryOptions(countryCode,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Import Branch Country/ZipCode assignment in CSV format<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding-backoffice
 */
export const useImportBranchAssignmentsHook = () => {
        const importBranchAssignments = useCustomWelcomeClient<void>();

        return useCallback((
    importBranchAssignmentsBody: ImportBranchAssignmentsBody,
 signal?: AbortSignal
) => {

        const formData = new FormData();
if(importBranchAssignmentsBody.File !== undefined) {
 formData.append(`File`, importBranchAssignmentsBody.File);
 }

        return importBranchAssignments(
          {url: `/branches/import`, method: 'PUT',
      headers: {'Content-Type': 'multipart/form-data', },
       data: formData, signal
    },
          );
        }, [importBranchAssignments])
      }




export const useImportBranchAssignmentsMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useImportBranchAssignmentsHook>>>, TError,ImportBranchAssignmentsMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useImportBranchAssignmentsHook>>>, TError,ImportBranchAssignmentsMutationVariables, TContext> => {

const mutationKey = ['importBranchAssignments'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const importBranchAssignments =  useImportBranchAssignmentsHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useImportBranchAssignmentsHook>>>, ImportBranchAssignmentsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  importBranchAssignments(data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ImportBranchAssignmentsMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useImportBranchAssignmentsHook>>>>
    export type ImportBranchAssignmentsMutationBody = ImportBranchAssignmentsBody
    export type ImportBranchAssignmentsMutationError = ErrorType<ProblemDetails>
    export type ImportBranchAssignmentsMutationVariables = {data: ImportBranchAssignmentsBody}

    export const useImportBranchAssignments = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useImportBranchAssignmentsHook>>>, TError,ImportBranchAssignmentsMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useImportBranchAssignmentsHook>>>,
        TError,
        ImportBranchAssignmentsMutationVariables,
        TContext
      > => {
      return useMutation(useImportBranchAssignmentsMutationOptions(options), queryClient);
    }
